import { Outlet, redirect } from 'react-router'
import { match } from 'ts-pattern'
import { withoutTrailingSlash } from 'ufo'
import { getMayTokenBeValid } from 'src/utils/token'
import { track } from 'src/utils/tracker'
import { MembershipPageTab } from '~/pages/Membership/constants'
import { createMembershipRouteHandle } from '~/pages/Membership/route-utils'
import ErrorPage from '../components/ErrorPage'
import { ALLOWED_GENERATOR_TABS } from '../components/GenerationTask/MultiColumnGenerationForm/constants'
import { ALLOWED_IMAGE_STITCHING_MODES } from '../components/GenerationTask/MultiColumnGenerationForm/ImageStitching/constants'
import LoadingPage from '../components/LoadingPage'
import {
  RankingContentType,
  RankingRangeType,
} from '../components/Ranking/ranking-utils'
import Root from '../pages/Root'
import type { AppRouteHandle, AppRouteObject } from '../utils/router/route'
import {
  applyLocalePrefix,
  createRedirect,
  createUserProfileRoute,
  passProps,
  userProfileHandle,
  userProfileLoader,
} from '../utils/router/route'
import { isValidId } from '../utils/validation/id'

export type { AppRouteObject, AppRouteHandle }

export const routes: AppRouteObject[] = [
  {
    handle: { name: 'root' },
    errorElement: <ErrorPage />,
    element: <Root />,
    hydrateFallbackElement: <LoadingPage fullscreen />,
    path: ':lang',
    middleware: [applyLocalePrefix],
    loader: applyLocalePrefix,
    children: [
      {
        handle: { name: 'error' },
        path: 'error',
        element: <ErrorPage />,
      },
      {
        handle: { name: 'login', track: { scene: 'login' } },
        path: 'login',
        lazy: () => passProps(import('../pages/SignPage'), { isLogin: true }),
      },
      {
        handle: { name: 'sign-up', track: { scene: 'sign-up' } },
        path: 'sign-up',
        lazy: () => import('../pages/SignPage'),
      },
      {
        handle: { name: 'verify-email' },
        path: 'verify-email',
        lazy: () => import('../pages/VerifyEmail'),
      },
      {
        handle: { name: 'reset-password' },
        path: 'reset-password',
        lazy: () => import('../pages/ResetPassword'),
      },
      {
        handle: { name: 'forgot-password/reset' },
        path: 'forgot-password/reset',
        lazy: () =>
          passProps(import('../pages/ResetPassword'), {
            isForgotPassword: true,
          }),
      },
      {
        handle: { name: 'change-email' },
        path: 'change-email',
        lazy: () => import('../pages/ChangeEmail'),
      },
      {
        handle: { name: 'forgot-password' },
        path: 'forgot-password',
        lazy: () => import('../pages/ForgotPassword'),
      },
      {
        handle: { name: 'forget-password/reset-legacy' },
        path: 'forget-password/reset',
        loader: ({ request, params }) =>
          createRedirect(request, `/${params.lang}/forgot-password/reset`),
      },
      {
        handle: { name: 'forget-password-legacy' },
        path: 'forget-password',
        loader: ({ params }) => redirect(`/${params.lang}/forgot-password`),
      },
      {
        handle: { name: 'user-revival' },
        path: 'user-revival',
        lazy: () => import('../pages/User/UserRevivalPage'),
      },
      {
        handle: { name: 'external-link', track: { scene: 'external-link' } },
        path: 'external-link',
        lazy: () => import('../pages/UrlWarningPage'),
      },
      {
        handle: { name: 'oauth2-authorize' },
        path: 'oauth2/:applicationSlug/authorize',
        lazy: () => import('../pages/OAuth2ConsentPage'),
      },
      {
        handle: { name: 'oauth2-device' },
        path: 'oauth2/:applicationSlug/device',
        lazy: () => import('../pages/OAuth2DevicePage'),
      },
      {
        handle: { name: 'app-layout' },
        lazy: () => import('../pages/AppPage'),
        children: [
          {
            handle: {
              name: 'home',
              track: { scene: 'home' },
              showAppNav: true,
            },
            path: '',
            lazy: () => import('../pages/AppPage/HomePageWrapper'),
          },

          {
            handle: { name: 'bot/discord/landing' },
            path: 'bot/discord/landing',
            lazy: () => import('../pages/DiscordBotEntrypoint'),
          },
          {
            handle: { name: 'profile/edit' },
            path: 'profile/edit',
            lazy: () => import('../pages/ProfileEditPage'),
            children: [
              {
                handle: {
                  name: 'profile/edit/basic',
                  track: { scene: 'profile-edit', section: 'basic' },
                },
                path: 'basic',
                lazy: () =>
                  import('../components/UserProfile/ProfileEdit/BasicProfile'),
              },
              {
                handle: {
                  name: 'profile/edit/accounts',
                  track: { scene: 'profile-edit', section: 'accounts' },
                },
                path: 'accounts',
                lazy: () =>
                  import('../components/UserProfile/ProfileEdit/Accounts'),
              },
              {
                handle: {
                  name: 'profile/edit/api',
                  track: { scene: 'profile-edit', section: 'api' },
                },
                path: 'api',
                lazy: () =>
                  import('../components/UserProfile/ProfileEdit/ApiPlatform'),
              },
              {
                handle: {
                  name: 'profile/edit/security',
                  track: { scene: 'profile-edit', section: 'security' },
                },
                path: 'security',
                lazy: () =>
                  import('../components/UserProfile/ProfileEdit/AccountSecurity'),
              },
              {
                handle: {
                  name: 'profile/edit/notification',
                  track: { scene: 'profile-edit', section: 'notification' },
                },
                path: 'notification',
                lazy: () =>
                  import('../components/UserProfile/ProfileEdit/Notification'),
              },
              {
                handle: {
                  name: 'profile/edit/safety',
                  track: { scene: 'profile-edit', section: 'content' },
                },
                path: 'safety',
                lazy: () =>
                  import('../components/UserProfile/ProfileEdit/ContentSettings'),
              },
              {
                handle: {
                  name: 'profile/edit/misc',
                  track: { scene: 'profile-edit', section: 'misc' },
                },
                path: 'misc',
                lazy: () =>
                  import('../components/UserProfile/ProfileEdit/MiscSettings'),
              },
              {
                path: '',
                handle: { name: 'profile/edit/' },
                loader: ({ request }) =>
                  createRedirect(request, '/profile/edit/basic'),
              },
              {
                handle: {
                  name: 'profile/edit/blocklist',
                  track: { scene: 'profile-edit', section: 'blocklist' },
                },
                path: 'blocklist',
                lazy: () =>
                  import('../components/UserProfile/ProfileEdit/BlockList'),
              },
            ],
          },
          {
            handle: {
              name: 'user-referral',
              track: { scene: 'user-referral' },
            },
            path: 'user-referral',
            lazy: () => import('../pages/User/UserReferralPage'),
          },
          {
            handle: { name: 'referral' },
            path: 'referral',
            lazy: () => import('../pages/User/ReferralOnboardingPage'),
          },
          {
            handle: {
              name: 'submit/upload',
              track: { scene: 'upload-artwork' },
            },
            path: 'submit/upload',
            lazy: () => import('../pages/ArtworkSubmitUploadPage'),
          },
          // We should have a path that acts as the home page for artworks like
          // `/market`.
          // Even if we are using `/` to do the job.
          {
            handle: { name: 'artwork-home' },
            path: 'artworks',
            loader: ({ params }) => {
              if (params.lang?.startsWith('@')) {
                // Redirect to the home page of the user's profile
                throw redirect(`/en/${params.lang}/artworks`)
              } else {
                throw redirect('/')
              }
            },
          },
          {
            handle: {
              name: 'artwork-detail',
              track: { scene: 'artwork-detail' },
              showAppNav: true,
            },
            path: 'artwork/:idSlug',
            lazy: () => import('../pages/ArtworkDetailPage'),
          },
          {
            handle: {
              name: 'video-artwork-detail',
              track: { scene: 'artwork-detail' },
              showAppNav: true,
            },
            path: 'video/:idSlug',
            lazy: () => import('../pages/ArtworkDetailPage'),
          },
          {
            handle: {
              name: 'artwork-relevant-artworks',
              track: { scene: 'artwork-relevant' },
              showAppNav: true,
            },
            path: 'artwork/:id/relevant',
            lazy: () => import('../pages/ArtworkRelevantArtworksPage'),
          },
          {
            handle: { name: 'tags-home', showAppNav: true },
            path: 'tags',
            lazy: () => import('../pages/AppPage/TrendingTagsPage'),
          },
          {
            handle: {
              name: 'tags/artwork',
              track: { scene: 'tags-artwork' },
              showAppNav: true,
            },
            path: 'tags/artwork/:tag',
            lazy: () =>
              passProps(import('../pages/AppPage/TagPage'), {
                type: 'artwork',
              }),
          },
          {
            handle: {
              name: 'tags/model',
              track: { scene: 'tags-model' },
              showAppNav: true,
            },
            path: 'tags/model/:tag',
            lazy: () =>
              passProps(import('../pages/AppPage/TagPage'), { type: 'model' }),
          },
          // Legacy routes - redirect to new tag URLs
          {
            handle: { name: 'artwork/tags' },
            path: 'artwork/tags/:tag?',
            loader: ({ request, params }) => {
              if (!params.tag) {
                return redirect('/')
              }
              track('legacy_route', { pathname: '/artwork/tags/*' })
              const url = new URL(request.url)
              url.pathname = url.pathname.replace(
                /\/artwork\/tags\//,
                '/tags/artwork/',
              )
              return redirect(url.href)
            },
          },
          {
            handle: { name: 'model/tags' },
            path: 'model/tags/:tag?',
            loader: ({ request, params }) => {
              if (!params.tag) {
                return redirect('/')
              }
              track('legacy_route', { pathname: '/model/tags/*' })
              const url = new URL(request.url)
              url.pathname = url.pathname.replace(
                /\/model\/tags\//,
                '/tags/model/',
              )
              return redirect(url.href)
            },
          },
          {
            handle: {
              name: 'model-detail',
              track: { scene: 'model-detail' },
              showAppNav: true,
            },
            path: 'model/:modelIdSlug/:versionId?',
            lazy: () => import('../pages/ModelDetailPage'),
          },
          {
            handle: {
              name: 'market',
              track: { scene: 'models' },
              showAppNav: true,
            },
            path: 'market',
            lazy: () => import('../pages/AppPage/MarketPage'),
          },
          {
            handle: { name: 'market/submit', showAppNav: true },
            path: 'market/submit',
            lazy: () => import('../pages/ModelSubmitPage'),
            children: [
              {
                handle: { name: 'market/submit/step-1', showAppNav: true },
                path: 'step-1',
                lazy: () =>
                  import('src/components/Market/ModelUploadPages/ModelUploadStepOne'),
              },
              {
                handle: { name: 'market/submit/step-2', showAppNav: true },
                path: 'step-2',
                lazy: () =>
                  import('src/components/Market/ModelUploadPages/ModelUploadStepTwo'),
              },
              {
                handle: { name: 'market/submit/step-3', showAppNav: true },
                path: 'step-3',
                lazy: () =>
                  import('src/components/Market/ModelUploadPages/ModelUploadStepThree'),
              },
            ],
          },
          {
            handle: { name: 'ranking' },
            path: 'ranking/:type?/:filterType?',
            lazy: () => import('../pages/RankingPage'),
            loader: ({ request, params }) => {
              // oxfmt-ignore
              const VALID_CONTENT_TYPES = Object.values(
                RankingContentType,
              ) as string[];
              // oxfmt-ignore
              const VALID_RANGE_TYPES = Object.values(
                RankingRangeType,
              ) as string[];

              let { type, filterType } = params
              if (type && !VALID_CONTENT_TYPES.includes(type)) {
                type = undefined
              }
              if (filterType && !VALID_RANGE_TYPES.includes(filterType)) {
                filterType = undefined
              }

              return type && filterType
                ? null
                : createRedirect(
                    request,
                    `/ranking/${type ?? RankingContentType.Artwork}/${filterType ?? RankingRangeType.Today}`,
                  )
            },
          },
          {
            handle: { name: 'search' },
            path: 'search',
            lazy: () => import('../pages/AppPage/SearchResultPage'),
          },
          {
            handle: { name: 'train-lora', track: { scene: 'train-lora' } },
            path: 'train-lora',
            lazy: () => import('../pages/TrainLoraPage'),
          },

          // Membership & payment

          {
            handle: {
              name: 'membership',
              hideMembershipBanner: true,
              showCreditPill: true,
            },
            path: 'membership',
            lazy: () => import('../pages/Membership/MembershipPlansPage'),
            children: [
              {
                handle: { name: 'membership-shortcut' },
                path: '',
                loader: () => redirect('/membership/plans'),
              },
              {
                handle: createMembershipRouteHandle({
                  name: 'membership/plans',
                  track: { scene: 'membership' },
                  hideMembershipBanner: true,
                  showCreditPill: true,
                  pageTab: MembershipPageTab.plans,
                }),
                path: 'plans',
                loader: ({ request }) => {
                  // Handle legacy and known query
                  const url = new URL(request.url)
                  if (url.searchParams.has('mode')) {
                    const mode = url.searchParams.get('mode')
                    url.searchParams.delete('mode')
                    if (mode === 'packs') {
                      track('legacy_route', {
                        pathname: '/membership/plans?mode=packs',
                      })
                      url.pathname = '/membership/credit-packs'
                    }
                    return redirect(url.href)
                  }
                  return null
                },
              },
              {
                handle: createMembershipRouteHandle({
                  name: 'membership/credit-packs',
                  track: { scene: 'credit-packs' },
                  hideMembershipBanner: true,
                  showCreditPill: true,
                  pageTab: MembershipPageTab.packs,
                }),
                path: 'credit-packs',
              },
            ],
          },
          {
            handle: {
              name: 'mio2/gem-pack',
              hideMembershipBanner: true,
              showCreditPill: true,
            },
            path: 'mio2/gem-pack',
            lazy: () => import('../pages/Kotone/KotoneCreditPackPage'),
          },
          {
            handle: { name: 'membership/current', hideMembershipBanner: true },
            path: 'membership/current',
            loader: ({ request }) => {
              track('legacy_route', { pathname: '/membership/current' })
              return createRedirect(request, '/me/credits')
            },
          },
          // Subscription result back from stripe page
          // Subscription doesn't have order ID
          {
            handle: {
              name: 'payment/subscription/landing',
              hideMembershipBanner: true,
              showCreditPill: true,
            },
            path: 'payment/subscription/landing',
            lazy: () => import('../pages/Membership/SubscriptionLanding'),
          },
          // Results are known, not getting back from stripe
          // Only subscription results are known for now
          {
            handle: {
              name: 'payment/result',
              hideMembershipBanner: true,
              showCreditPill: true,
            },
            path: 'payment/subscription/result',
            lazy: () => import('../pages/Membership/SubscriptionUpdateResult'),
          },
          {
            handle: {
              name: 'payment/landing',
              hideMembershipBanner: true,
              showCreditPill: true,
            },
            path: 'payment/landing',
            lazy: () => import('../pages/Membership/PaymentLanding'),
          },

          {
            handle: {
              name: 'eap',
              hideAppFooter: true,
              hideMembershipBanner: true,
            },
            path: 'eap',
            lazy: () => import('../pages/EapPage'),
          },

          {
            handle: { name: 'credit-exchange-redeem' },
            path: 'credit-exchange/redeem',
            lazy: () => import('../clientRoutes/_app.credit-exchange.redeem'),
          },

          // User Profile

          // User routes don't start with `@` since https://github.com/remix-run/react-router/pull/9506
          {
            path: ':usernameRaw',
            handle: userProfileHandle,
            element: <Outlet />,
            children: [
              {
                handle: { name: 'bookmark-artwork' },
                path: 'collection/:collectionId',
                lazy: () => import('../pages/UserProfilePage'),
              },
              {
                path: '',
                handle: userProfileHandle,
                lazy: () => import('../pages/UserProfilePage'),
                loader: userProfileLoader,
              },
              {
                path: 'artworks/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                // ⬆️ Just a hint for build time AST analysis.
                // Actual: () => passProps(import('./pages/UserProfilePage'), { tab: 'artworks' }),
                ...createUserProfileRoute('artworks'),
              },
              {
                path: 'likes/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('likes'),
              },
              {
                path: 'tasks/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('tasks'),
              },
              {
                path: 'credits/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('credits'),
              },
              {
                path: 'assets/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('assets'),
              },
              {
                path: 'theme/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('theme'),
              },
              {
                path: 'models/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('models'),
              },
              {
                path: 'bookmarked-models/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('bookmarked-models'),
              },
              {
                path: 'manga/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('manga'),
              },
              {
                path: 'liked-manga/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('liked-manga'),
              },
              {
                path: 'liked-sdv1-model/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('liked-sdv1-model'),
              },
              {
                path: 'liked-model/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('liked-model'),
              },
              {
                path: 'liked-lora-model/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('liked-lora-model'),
              },
              {
                path: 'liked-lora/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('liked-lora'),
              },
              {
                path: 'favorites/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('favorites'),
              },
              {
                path: 'files/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('files'),
              },
              {
                path: 'workflows/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('workflows'),
              },
              {
                path: 'decorations/:subTab?',
                lazy: () => import('../pages/UserProfilePage'),
                ...createUserProfileRoute('decorations'),
              },
            ],
          },
          {
            ...createUserProfileRoute('follower'),
            path: ':usernameRaw/follower/:subTab?',
          },
          {
            ...createUserProfileRoute('following'),
            path: ':usernameRaw/following/:subTab?',
          },
          {
            handle: { name: 'me/*' },
            path: 'me/*',
            lazy: () => import('../pages/MeRoute'),
          },
          // Will redirect to @ username route
          // For internal use only for now
          {
            handle: { name: 'user/:id/*' },
            path: 'user/:id/*',
            lazy: () => import('../pages/User/UserIdRedirectPage'),
            loader: ({ request, params }) => {
              if (params.id && isValidId(params.id)) {
                return null
              }
              return createRedirect(request, '/')
            },
          },

          {
            handle: {
              name: 'news-list',
              track: { scene: 'news', section: 'list' },
            },
            path: 'news',
            lazy: () => import('src/pages/NewsListPage'),
          },
          {
            handle: {
              name: 'news-detail',
              track: ({ params }) => ({ scene: 'news', section: params.id }),
            },
            path: 'news/:id',
            lazy: () => import('../pages/NewsDetailPage'),
          },

          // Contest V2

          {
            handle: {
              name: 'contest-overview',
              track: { scene: 'contest' },
              showAppNav: true,
            },
            path: 'contest',
            lazy: () => import('../clientRoutes/_app.contest._index'),
          },
          {
            handle: {
              name: 'contest-official',
              track: { scene: 'contest' },
              showAppNav: true,
            },
            path: 'contest/official',
            lazy: () => import('../clientRoutes/_app.contest.official'),
          },
          {
            handle: {
              name: 'contest-community',
              track: { scene: 'contest' },
              showAppNav: true,
            },
            path: 'contest/community',
            lazy: () => import('../clientRoutes/_app.contest.community'),
          },
          {
            handle: {
              name: 'contest-create',
              track: { scene: 'contest' },
              showAppNav: true,
            },
            path: 'contest/create',
            lazy: () => import('../clientRoutes/_app.contest.create'),
          },
          {
            handle: {
              name: 'contest-detail',
              track: ({ params }) => ({ scene: 'contest', section: params.id }),
              showAppNav: true,
            },
            path: 'contest/:id',
            lazy: () => import('../clientRoutes/_app.contest.$slug'),
          },

          {
            handle: {
              name: 'events',
              hideMembershipBanner: true,
              track: ({ params }) => ({
                scene: 'event',
                section: params.eventRoute,
              }),
              // display for august event.
              // hideMembershipBanner: true,
            },
            path: 'event/:eventRoute/*',
            lazy: () => import('../pages/EventsPage'),
            loader: ({ params }) => {
              // Redirect archived event pages to perm-links if one exists
              const eventRoutePath = withoutTrailingSlash(
                `${params.eventRoute}/${params['*'] ?? ''}`,
              )
              switch (eventRoutePath) {
                case '3rd-anniversary/mio':
                  return redirect('/mio')
              }

              return null
            },
          },
        ],
      },

      // Cosmetic shop

      {
        handle: {
          name: 'shop-layout',
          track: () => ({
            scene: 'shop',
          }),
        },
        lazy: () => import('../features/DecorationShop/DecorationShopLayout'),
        children: [
          {
            path: 'style',
            handle: { name: 'style' },
            lazy: () => import('../features/DecorationShop/DecorationShopPage'),
          },
        ],
      },

      // Generator paths

      {
        handle: { name: 'generator' },
        path: 'generator',
        loader: ({ request }) => {
          return createRedirect(request, '/generator/image')
        },
      },
      {
        handle: {
          name: 'generator/image',
          track: { scene: 'generator-image' },
          hideMembershipBanner: true,
        },
        path: 'generator/image',
        lazy: () => import('../pages/MultiColumnSubmitPage'),
      },
      {
        handle: {
          name: 'generator/image/:tab',
          track: ({ params }) => ({
            scene: match(params.tab)
              .with('editor', () => 'generator-edit')
              .with('animation', () => 'generator-animation')
              .with('enhancement', () => 'generator-enhance')
              .otherwise(() => undefined),
          }),
          hideMembershipBanner: true,
        },
        path: 'generator/image/:tab',
        lazy: () => import('../pages/MultiColumnSubmitPage'),
        loader: ({ request, params }) => {
          // Ensure tab is known
          if (params.tab && !ALLOWED_GENERATOR_TABS.includes(params.tab)) {
            return createRedirect(request, '/generator/image')
          }
          return null
        },
      },
      {
        handle: {
          name: 'generator/realtime',
          track: { scene: 'realtime' },
          hideMembershipBanner: true,
        },
        path: 'generator/realtime/:mode?',
        lazy: async () => ({
          Component: (await import('src/components/FeatureRetiredPage'))
            .default,
        }),
        loader: ({ request, params }) => {
          if (params.mode !== 'drawing') {
            return createRedirect(request, '/generator/realtime/drawing')
          }
          return null
        },
      },
      {
        handle: { name: 'generator/agent' },
        path: 'generator/agent',
        lazy: () => import('../pages/AgentGeneratorPage'),
      },
      {
        handle: { name: 'generator/mio' },
        path: 'generator/mio',
        lazy: () => import('../pages/AgentGeneratorPage'),
      },
      {
        handle: { name: 'generator/upscaler', track: { scene: 'upscale' } },
        path: 'generator/upscaler',
        lazy: () => import('../pages/UpscalePage'),
      },
      {
        handle: { name: 'generator/image-stitching' },
        path: 'generator/image-stitching',
        loader: ({ request }) => {
          track('legacy_route', { pathname: '/generator/image-stitching' })
          return createRedirect(request, '/generator/stitching/kiss')
        },
      },
      {
        handle: {
          name: 'generator/stitching/:mode',
          track: ({ params }) => ({
            scene: 'image-stitching',
            section: params.mode,
          }),
        },
        path: 'generator/stitching/:mode',
        lazy: () => import('../pages/ImageStitchPage'),
        loader: ({ request, params }) => {
          const allowedModes =
            ALLOWED_IMAGE_STITCHING_MODES as readonly string[]
          if (params.mode && !allowedModes.includes(params.mode)) {
            return createRedirect(request, '/generator/stitching/kiss')
          }
          return null
        },
      },
      {
        handle: {
          name: 'generator/preset',
          track: { scene: 'ai-tools-landing' },
          showAppNav: true,
        },
        path: 'generator/preset',
        lazy: () => import('../pages/GeneratorPresetLandingPage'),
      },
      {
        handle: {
          name: 'generator/preset/:sceneId',
          track: ({ params }) => ({
            scene: 'chat-editing-preset',
            section: params.sceneId,
          }),
          showAppNav: true,
        },
        path: 'generator/preset/:sceneId',
        lazy: () => import('../pages/ChatEditingPresetPage'),
      },
      {
        handle: {
          name: 'generator/chat-editing',
          track: { scene: 'chat-editing', section: 'preview' },
        },
        path: 'generator/chat-editing',
        lazy: () => import('../pages/ConversationalEditPage'),
      },
      {
        handle: { name: 'generator/chat-editing/creator' },
        path: 'generator/chat-editing/creator',
        lazy: () => import('../pages/ConversationalEditPage'),
      },
      {
        handle: {
          name: 'generator/chat-editing/workspace/:workspaceId',
          track: { scene: 'chat-editing', section: 'panel' },
        },
        path: 'generator/chat-editing/workspace/:workspaceId',
        lazy: () => import('../pages/ConversationalEditPage'),
      },

      // Single pages

      // Survey page (Typeform integration)
      {
        handle: { name: 'survey', track: { scene: 'survey' } },
        path: 'survey/:formId',
        lazy: () => import('../pages/SurveyPage'),
      },

      {
        handle: { name: 'terms-layout' },
        lazy: () => import('../layouts/TermsLayout'),
        children: [
          {
            handle: { name: 'terms' },
            path: 'terms',
            lazy: () => import('../pages/Terms'),
          },
          {
            handle: { name: 'privacy' },
            path: 'privacy',
            lazy: () => import('../pages/Privacy'),
          },
          {
            handle: { name: 'legal-for-payment' },
            path: 'legal',
            lazy: () => import('../pages/LegalForPayment'),
          },
          {
            handle: { name: 'copyright' },
            path: 'copyright',
            lazy: () => import('../pages/CopyrightPolicyPage'),
          },
          {
            handle: { name: 'feedback' },
            path: 'feedback',
            lazy: () => import('../pages/Feedback'),
          },
          {
            handle: { name: 'mio' },
            path: 'mio',
            lazy: () => import('../pages/MioPage'),
          },
        ],
      },
      {
        handle: {
          name: 'year-recap',
          track: { scene: 'event', section: 'year-recap-2025' },
        },
        path: 'recap/:year?',
        lazy: () => import('src/pages/YearRecap'),
        loader: ({ request, params }) => {
          if (!getMayTokenBeValid()) {
            return createRedirect(request, '/login', {
              to: new URL(request.url).pathname,
            })
          }

          // Right now we only allow the current year.
          // if (!params.year || !(params.year in yearRecapPages)) {
          //   return redirect('/')
          // }

          return { year: 2025 }
        },
      },
      // Mistakenly used path. Redirect to the correct one to maintain the traffic
      {
        handle: { name: '__year-recap' },
        path: 'my2025',
        loader: ({ request }) => {
          track('legacy_route', { pathname: '/my2025' })
          return createRedirect(request, '/recap')
        },
      },

      // Embedded pages

      {
        handle: { name: 'embedded-layout' },
        lazy: () => import('src/layouts/EmbeddedLayout'),
        children: [
          // Embedded Event Page, e.g. for mobile app
          {
            handle: {
              name: 'embedded-event-page',
              hideMembershipBanner: true,
              track: ({ params }) => ({
                scene: 'event',
                section: params.eventRoute,
              }),
            },
            path: 'embedded/event/:eventRoute/*',
            lazy: () => import('../pages/Embedded/EmbeddedEventPage'),
          },
          {
            handle: { name: 'embedded-referral-page' },
            path: 'embedded/user-referral',
            lazy: () => import('../pages/Embedded/EmbeddedReferralPage'),
          },

          // Conversational Edit
          {
            handle: { name: 'embedded-chat-editing' },
            path: 'embedded/chat-editing',
            lazy: () =>
              import('../pages/Embedded/EmbeddedConversationalEditPage'),
          },
          {
            handle: { name: 'embedded-chat-editing-creator' },
            path: 'embedded/chat-editing/creator',
            lazy: () =>
              import('../pages/Embedded/EmbeddedConversationalEditPage'),
          },
          {
            handle: { name: 'embedded-chat-editing-workspace' },
            path: 'embedded/chat-editing/workspace/:workspaceId',
            lazy: () =>
              import('../pages/Embedded/EmbeddedConversationalEditPage'),
          },

          // News
          {
            handle: { name: 'embedded-news' },
            path: 'embedded/news/:id',
            lazy: () => import('../pages/Embedded/EmbeddedNewsPage'),
          },
        ],
      },

      // Redirect legacy paths

      // {
      //   handle: { name: 'tasks' },
      //   path: 'tasks',
      //   loader: ({ request }) => {
      //     track('legacy_route', { pathname: '/tasks' })
      //     return createRedirect(request, '/me/tasks')
      //   },
      // },
      {
        handle: { name: 'realtime' },
        path: 'realtime',
        loader: ({ request }) => {
          track('legacy_route', { pathname: '/realtime' })
          return createRedirect(request, '/generator/realtime')
        },
      },
      {
        handle: { name: 'submit' },
        path: 'submit',
        loader: ({ request }) => {
          track('legacy_route', { pathname: '/submit' })
          return createRedirect(request, '/generator')
        },
      },
      {
        handle: { name: 'submit/gen' },
        path: 'submit/gen',
        loader: ({ request }) => {
          track('legacy_route', { pathname: '/submit/gen' })
          return createRedirect(request, '/generator/image', { mode: 'gen' })
        },
      },
      {
        handle: { name: 'submit/fill' },
        path: 'submit/fill',
        loader: ({ request }) => {
          track('legacy_route', { pathname: '/submit/fill' })
          return createRedirect(request, '/generator/image', { mode: 'fill' })
        },
      },

      // Paid UA Landing page
      {
        handle: {
          name: 'landing',
          track: { scene: 'landing', section: 'landing' },
        },
        path: 'landing',
        lazy: () => import('../clientRoutes/landing'),
        children: [
          {
            handle: { name: 'landing/welcome' },
            path: '',
            lazy: () => import('../clientRoutes/landing.welcome._index'),
          },
          {
            handle: { name: 'landing/welcome' },
            path: 'welcome',
            lazy: () => import('../clientRoutes/landing.welcome._index'),
          },
        ],
      },

      // Tsubaki.2 Landing page
      {
        handle: {
          name: 'tsubaki-2',
          track: { scene: 'model-landing', section: 'tsubaki-2' },
        },
        path: 'tsubaki-2',
        lazy: () => import('../clientRoutes/tsubaki-2'),
        children: [
          {
            handle: { name: 'tsubaki-2/home' },
            path: '',
            lazy: () => import('../clientRoutes/tsubaki-2._index'),
          },
          {
            handle: { name: 'tsubaki-2/guide' },
            path: 'guide',
            lazy: () => import('../clientRoutes/tsubaki-2.guide'),
          },
          {
            handle: { name: 'tsubaki-2/styles' },
            path: 'styles',
            lazy: () => import('../clientRoutes/tsubaki-2.styles'),
          },
          {
            handle: { name: 'tsubaki-2/gallery' },
            path: 'gallery',
            lazy: () => import('../clientRoutes/tsubaki-2.gallery'),
          },
        ],
      },

      // 404 page
      {
        handle: { name: '404' },
        path: '404',
        lazy: async () => ({
          Component: (await import('src/components/NotFoundPage')).default,
        }),
      },

      // Redirect unknown paths to /404
      {
        handle: { name: 'catch-all' },
        path: '*',
        loader: ({ request }) => {
          return createRedirect(request, '/404')
        },
      },
    ],
  },
  {
    handle: { name: 'locale-fallback' },
    path: '*',
    loader: applyLocalePrefix,
  },
  ...(import.meta.env.DEV
    ? [
        {
          handle: { name: '' },
          path: '/playground',
          lazy: () => import('../pages/playground'),
        },
      ]
    : []),
  // {
  //   handle: { name: 'redirect-loop' },
  //   path: '/redirect-loop',
  //   loader: () => redirect('/redirect-loop'),
  // },
  // {
  //   handle: { name: 'check-circular' },
  //   path: '/check-circular',
  //   lazy: () => import('./pages/CheckCircularPage'),
  // },
]
